import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Janela modal.
 *
 * Vai para o `document.body` por portal: dentro da árvore da página ela
 * herdaria o contexto de empilhamento do `main`, que tem `overflow-y: auto`, e
 * um diálogo posicionado por `fixed` acabaria recortado pelo scroll.
 *
 * Não usa `<dialog>` nativo porque o `::backdrop` não aceita as classes do
 * Tailwind e o modo escuro ficaria fora do sistema de tokens.
 */
export function Modal({
  titulo,
  descricao,
  aberto,
  aoFechar,
  children,
}: {
  titulo: string;
  descricao?: string;
  aberto: boolean;
  aoFechar: () => void;
  children: ReactNode;
}) {
  const tituloId = useId();
  const painel = useRef<HTMLDivElement>(null);
  const focoMovido = useRef(false);

  useEffect(() => {
    if (!aberto) {
      focoMovido.current = false;
      return;
    }

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoTeclar);

    // Trava a rolagem de fundo: rolar a lista atrás do diálogo faz o usuário
    // perder o formulário de vista sem perceber que ele continua aberto.
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Foco no primeiro campo apenas na abertura — não rouba foco em re-renders
    // causados por atualização de props (ex: aoFechar recebendo nova referência).
    if (!focoMovido.current) {
      focoMovido.current = true;
      painel.current?.querySelector<HTMLElement>('input, textarea, select, button')?.focus();
    }

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = anterior;
    };
  }, [aberto, aoFechar]);

  if (!aberto) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div
        className="fixed inset-0 bg-foreground/30 backdrop-blur-[2px]"
        // Fechar pelo fundo é conveniência do mouse; o teclado tem o Esc e o
        // botão de fechar, então este nó não precisa ser focável.
        onClick={aoFechar}
        aria-hidden
      />

      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        className="relative w-full max-w-lg animate-slide-up rounded-lg border border-border bg-card shadow-elevation"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 id={tituloId} className="font-heading text-base font-semibold leading-tight">
              {titulo}
            </h2>
            {descricao ? <p className="mt-1 text-sm text-muted-foreground">{descricao}</p> : null}
          </div>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="grid h-8 w-8 flex-none place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {children}
      </div>
    </div>,
    document.body,
  );
}
